import { useEffect, useRef, useState } from 'react';

/**
 * Live extension status, pushed from the backend over Server-Sent Events.
 *
 * The companion to useLiveData's polling: where a figure only needs to be
 * roughly current, a status dot is something someone acts on — "is my phone
 * online, is that agent on a call right now" — so it's pushed, not polled. The
 * backend holds one AMI connection open and forwards each DeviceStateChange the
 * instant it happens (see AmiClient::streamDeviceStates).
 *
 * Returns a map keyed by extension, in the same shape /extensions folds into
 * each row's `status`, so a page can simply prefer this map and fall back to
 * the last polled value:
 *
 *   const live = useLiveStatus();
 *   const status = live.byExtension[e.extension] ?? e.status;
 *
 * `connected` reflects whether the stream is currently up; `supported` goes
 * false when the backend says live status isn't available at all (AMI off), at
 * which point we stop reconnecting rather than hammer a dead channel.
 */

const BASE = import.meta.env.VITE_API_BASE || '';
const STREAM_URL = `${BASE}/api/extensions/status/stream`;

// A live device state means the handset is registered; only these two don't.
const OFFLINE_STATES = new Set(['offline', 'unknown']);

export function useLiveStatus() {
  const [byExtension, setByExtension] = useState({});
  const [connected, setConnected] = useState(false);
  const [supported, setSupported] = useState(true);

  const sourceRef = useRef(null);
  // Set once the backend tells us the channel is dead; blocks reconnects.
  const disabledRef = useRef(false);

  useEffect(() => {
    const connect = () => {
      if (disabledRef.current || sourceRef.current || document.visibilityState !== 'visible') {
        return;
      }

      // withCredentials so the session cookie rides along — same rule as the
      // fetch layer. The stream is scoped by that cookie and nothing else.
      const es = new EventSource(STREAM_URL, { withCredentials: true });
      sourceRef.current = es;

      es.onopen = () => setConnected(true);

      es.addEventListener('snapshot', (e) => {
        setByExtension(parse(e.data) ?? {});
      });

      es.addEventListener('state', (e) => {
        const change = parse(e.data);
        if (!change?.extension) return;
        setByExtension((prev) => ({
          ...prev,
          [change.extension]: merge(prev[change.extension], change.state),
        }));
      });

      // AMI isn't wired up on this backend — the dots will show whatever the
      // last /extensions payload carried. Stop here; don't reconnect.
      es.addEventListener('disabled', () => {
        disabledRef.current = true;
        setSupported(false);
        setConnected(false);
        disconnect();
      });

      es.onerror = () => {
        // The browser reconnects on its own after the retry interval; we only
        // reflect that we're momentarily down. Keep the last known dots up —
        // a blip shouldn't blank the roster.
        setConnected(false);
      };
    };

    const disconnect = () => {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
    };

    // Holding the stream open for a tab nobody's looking at holds a backend
    // worker and an AMI socket for nothing. Drop it when hidden, restore it —
    // with a fresh snapshot — when the page comes back.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        connect();
      } else {
        setConnected(false);
        disconnect();
      }
    };

    connect();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      disconnect();
    };
  }, []);

  return { byExtension, connected, supported };
}

function parse(data) {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/**
 * Fold a pushed state change onto the last known status for an extension.
 *
 * A delta only carries the new device state, so keep the rest of what the
 * snapshot told us (where it's connected from, latency) and derive the
 * registration flags the tooltip reads — otherwise a phone that just went
 * offline would still claim to be reachable.
 */
function merge(previous, state) {
  const registered = !OFFLINE_STATES.has(state);

  return {
    ...(previous ?? {}),
    state,
    registered,
    reachable: registered,
  };
}
